import React from 'react';
import Parse from 'parse';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import IosShareIcon from '@mui/icons-material/IosShare';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MediaItem } from '../experiences/LifeObjectRenderer';
import './MobileAppPreview.css';

// Ported from lifetagger-mobile/lib/src/viewer/life_object_viewer.dart:
// _FigmaMomentDetail, _ContentStoryViewer and _storyCompositions.
const textual = (item) => ['text', 'html'].includes(item.type);
const plain = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
export function mobileComposition(content = []) {
  const unique = [],
    indexes = new Map();
  for (const raw of content) {
    const body = raw.body ?? raw.text ?? raw.html ?? raw.bodyHtml;
    const type =
      raw.type === 'pdf'
        ? 'document'
        : raw.type === 'text' &&
          /<\s*(?:div|p|h[1-6]|span|img|ul)\b/i.test(body || '')
        ? 'html'
        : raw.type;
    const item = {
      ...raw,
      type,
      body,
      url: raw.assetPreviewUrl || raw.url || raw.assetUrl,
    };
    const message = plain(body).toLowerCase();
    const key =
      textual(item) && message
        ? `message|${message}`
        : [type, item.title || '', message, item.url || ''].join('|');
    if (!indexes.has(key)) {
      indexes.set(key, unique.length);
      unique.push(item);
    } else if (type === 'html' && unique[indexes.get(key)].type === 'text')
      unique[indexes.get(key)] = item;
  }
  const groups = new Map(),
    ungrouped = [];
  for (const item of unique) {
    const id = item.compositionId || item.sceneId;
    if (!id) ungrouped.push(item);
    else {
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    }
  }
  const stories = [...groups.values()];
  let pending = [];
  for (const item of ungrouped) {
    if (textual(item)) pending.push(item);
    else {
      stories.push([...pending, item]);
      pending = [];
    }
  }
  if (pending.length) stories.push(pending);
  return {
    content: unique,
    stories,
    composed: stories.length > 1 || stories.some((items) => items.length > 1),
  };
}
function Avatar({ name, src }) {
  return (
    <span className="lt-mobile-avatar">
      {src ? <img src={src} alt="" /> : name?.[0]?.toUpperCase() || '•'}
    </span>
  );
}
function MobileContent({ item }) {
  const player = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    setPlaying(false);
    setFailed(false);
  }, [item.url, item.type]);
  const media = ['audio', 'video'].includes(item.type);
  const external = ['document', 'link', 'file', 'url-embed'].includes(
    item.type
  );
  const safeUrl =
    typeof item.url === 'string' && /^https?:\/\//i.test(item.url);
  if ((media || external) && (!safeUrl || failed))
    return (
      <div className="lt-mobile-missing">
        <strong>
          {item.type[0].toUpperCase() + item.type.slice(1)} unavailable
        </strong>
        <p>This part could not load. The rest of the moment is still here.</p>
      </div>
    );
  const card = (icon, title, detail) => (
    <>
      {icon}
      <span>
        <strong>{title}</strong>
        <small>{detail}</small>
      </span>
      <ChevronRightIcon />
    </>
  );
  if (media)
    return (
      <div className="lt-mobile-media">
        {item.type === 'video' ? (
          <video
            ref={player}
            src={item.url}
            preload="metadata"
            playsInline
            onError={() => setFailed(true)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
          />
        ) : (
          <audio
            ref={player}
            src={item.url}
            preload="metadata"
            onError={() => setFailed(true)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
          />
        )}
        <button
          className="lt-mobile-content-card"
          onClick={() => {
            if (playing) player.current.pause();
            else player.current.play().catch(() => setFailed(true));
          }}
        >
          {card(
            playing ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />,
            item.title || (item.type === 'audio' ? 'Listen' : 'Watch video'),
            playing ? 'Playing' : item.type === 'audio' ? 'Audio' : 'Video'
          )}
        </button>
      </div>
    );
  if (external)
    return (
      <a
        className="lt-mobile-content-card"
        href={item.url}
        target="_blank"
        rel="noreferrer"
      >
        {card(
          item.type === 'document' ? (
            <DescriptionOutlinedIcon />
          ) : (
            <OpenInNewIcon />
          ),
          item.title ||
            (item.type === 'document' ? 'Open document' : 'Open link'),
          item.mimeType || item.type
        )}
      </a>
    );
  return <MediaItem item={item} />;
}
function MobileLocation({ anchor }) {
  const coordinates = anchor.coordinates || anchor.geoPoint || anchor;
  const lat = Number(coordinates.latitude ?? coordinates.lat),
    lng = Number(coordinates.longitude ?? coordinates.lng);
  const label =
    anchor.metadata?.placeLabel ||
    anchor.metadata?.address ||
    anchor.name ||
    'Approximate location';
  const valid =
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 85 &&
    Math.abs(lng) <= 180;
  const n = 1 << 14,
    rad = (lat * Math.PI) / 180;
  const x = Math.floor(((lng + 180) / 360) * n),
    y = Math.floor(
      ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n
    );
  return valid ? (
    <div className="lt-mobile-location">
      <div className="lt-mobile-map">
        <img
          src={`https://tile.openstreetmap.org/14/${x}/${y}.png`}
          alt={`Map near ${label}`}
        />
        <LocationOnOutlinedIcon />
        <strong>{label}</strong>
      </div>
      <a
        className="lt-mobile-map-link"
        href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
        target="_blank"
        rel="noreferrer"
      >
        Navigate to {label}
      </a>
    </div>
  ) : (
    <div className="lt-mobile-context">
      <LocationOnOutlinedIcon fontSize="small" />
      <div>
        <strong>{label}</strong>
        <small>Location approximate · No exact GPS coordinates</small>
      </div>
    </div>
  );
}
export default function MobileAppPreview({ lifeObject }) {
  const model = React.useMemo(
    () => mobileComposition(lifeObject.content),
    [lifeObject.content]
  );
  const [index, setIndex] = React.useState(0);
  const [conversation, setConversation] = React.useState(null);
  const [conversationState, setConversationState] = React.useState('loading');
  React.useEffect(() => {
    let current = true;
    setIndex(0);
    setConversation(null);
    setConversationState('loading');
    Parse.Cloud.run('getLifeObjectConversation', {
      lifeObjectId: lifeObject.id || lifeObject.objectId,
      limit: 30,
      page: 1,
    })
      .then((value) => {
        if (current) {
          setConversation(value);
          setConversationState('ready');
        }
      })
      .catch(() => {
        if (current) setConversationState('unavailable');
      });
    return () => {
      current = false;
    };
  }, [lifeObject.id, lifeObject.objectId]);
  const author =
    lifeObject.authorName ||
    lifeObject.author?.name ||
    lifeObject.metadata?.from ||
    lifeObject.owner?.name ||
    'LifeTagger member';
  const photo =
    lifeObject.authorProfileUrl ||
    lifeObject.author?.profileUrl ||
    lifeObject.author?.avatarUrl;
  const audience = lifeObject.audience || {};
  const count = Math.max(
    ...['identifiers', 'recipientKeys', 'recipientLabels'].map(
      (key) => audience[key]?.length || 0
    )
  );
  const direct = audience.visibility === 'named-users' && count <= 1;
  const group =
    ['group', 'channel'].includes(audience.visibility) ||
    (audience.visibility === 'named-users' && count > 1);
  const anchors = lifeObject.anchors || [];
  const spatial = anchors.find((anchor) => anchor.type === 'spatial');
  const location = anchors.find((anchor) =>
    ['geo', 'geo-point', 'geo-fence'].includes(anchor.type)
  );
  const hero = model.content.find((item) => item.type === 'image' && item.url);
  const visible = model.content.filter((item) => item !== hero);
  const narrative = visible.find(textual);
  const comments = conversation?.comments || [];
  const discussion = direct || group || comments.length > 0;
  const screenTitle = discussion
    ? 'Moment Discussion'
    : spatial
    ? 'Distant Placements'
    : location
    ? 'Nearby Discovery'
    : hero
    ? 'Photo Moment'
    : 'Solitary Moment';
  const badge = direct
    ? `PRIVATE · FROM ${author.toUpperCase()}`
    : group
    ? `GROUP · ${(
        audience.name ||
        audience.label ||
        'Private group'
      ).toUpperCase()}`
    : spatial
    ? 'IN SPACE'
    : anchors.some((anchor) => anchor.type === 'qr')
    ? 'LIFECODE · SCANNED'
    : anchors.length > 1
    ? 'CONTEXTUAL MOMENT'
    : location
    ? 'FOUND NEARBY'
    : lifeObject.contextLabel || 'MOMENT REVEALED';
  const name = lifeObject.name || lifeObject.title || '';
  const title =
    !['', 'a lifetagger moment', 'lifetagger moment', 'a moment'].includes(
      name.trim().toLowerCase()
    ) &&
    !model.content.some(
      (item) => plain(item.body).toLowerCase() === plain(name).toLowerCase()
    )
      ? name
      : null;
  const rawDate =
    lifeObject.sourceCreatedAt ||
    lifeObject.metadata?.sourceCreatedAt ||
    lifeObject.createdAt;
  const date = rawDate ? new Date(rawDate) : null;
  const dateLabel =
    date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : 'Shared recently';
  const story =
    model.stories[Math.min(index, Math.max(0, model.stories.length - 1))] || [];
  const primary = story.find((item) => !textual(item)),
    caption = story.find(textual);
  return (
    <section
      className="lt-mobile-app"
      data-testid="mobile-app-detail"
      aria-label="Mobile app LifeObject detail"
    >
      <header className="lt-mobile-header">
        <ChevronLeftIcon aria-hidden="true" />
        <strong>LifeTagger</strong>
        <span>{screenTitle}</span>
      </header>
      <div className="lt-mobile-scroll">
        {model.composed ? (
          <div className="lt-mobile-story" data-testid="mobile-content-stories">
            <div className="lt-mobile-story-content">
              {primary ? (
                <MobileContent item={primary} />
              ) : caption ? (
                <MobileContent item={caption} />
              ) : null}
            </div>
            {primary && caption && (
              <p className="lt-mobile-story-caption">{plain(caption.body)}</p>
            )}
            <div className="lt-mobile-progress">
              {model.stories.map((_, i) => (
                <span key={i} className={i <= index ? 'filled' : ''} />
              ))}
            </div>
            <div className="lt-mobile-story-author">
              <Avatar name={author} src={photo} />
              <strong>{author}</strong>
              <small>
                {index + 1} / {model.stories.length}
              </small>
            </div>
            <div className="lt-mobile-story-nav">
              <button
                aria-label="Previous content"
                disabled={index === 0}
                onClick={() => setIndex((value) => value - 1)}
              >
                ‹
              </button>
              <button
                aria-label="Next content"
                disabled={index === model.stories.length - 1}
                onClick={() => setIndex((value) => value + 1)}
              >
                ›
              </button>
            </div>
            <span className="lt-mobile-story-context">
              <LocationOnOutlinedIcon fontSize="inherit" /> {badge}
            </span>
          </div>
        ) : hero ? (
          <div className="lt-mobile-hero">
            <MobileContent item={hero} />
          </div>
        ) : null}
        <article
          className={`lt-mobile-moment ${
            hero && !model.composed ? 'has-hero' : ''
          }`}
        >
          <div className="lt-mobile-author">
            <Avatar name={author} src={photo} />
            <strong>{author}</strong>
            <span className="lt-mobile-badge">{badge}</span>
          </div>
          <div className="lt-mobile-date">{dateLabel}</div>
          {title && <h2>{title}</h2>}
          {!model.composed && narrative && (
            <div className="lt-mobile-narrative">
              <MobileContent item={narrative} />
            </div>
          )}
          {!title && !narrative && !model.stories.length && (
            <p>No content available</p>
          )}
          {location && <MobileLocation anchor={location} />}
          {spatial && (
            <div className="lt-mobile-context is-spatial">
              <ViewInArOutlinedIcon fontSize="small" /> Placed in{' '}
              {spatial.metadata?.placeLabel ||
                spatial.name ||
                'a physical space'}
            </div>
          )}
          <div className="lt-mobile-actions">
            <button disabled>
              <BookmarkBorderIcon /> Save
            </button>
            <button disabled>
              <IosShareIcon /> Share
            </button>
            {discussion && (
              <button disabled>
                <ChatBubbleOutlineIcon /> Discuss
              </button>
            )}
          </div>
        </article>
        {!model.composed &&
          visible
            .filter((item) => item !== narrative)
            .map((item, i) => (
              <div className="lt-mobile-supplement" key={i}>
                <MobileContent item={item} />
              </div>
            ))}
        <div className="lt-mobile-conversation">
          {conversationState === 'loading' ? (
            <p>Loading conversation…</p>
          ) : conversationState === 'unavailable' ? (
            <p>Conversation unavailable in this preview.</p>
          ) : comments.length ? (
            <>
              <h3>
                CONVERSATION (
                {conversation.summary?.commentCount || comments.length}{' '}
                THOUGHTS)
              </h3>
              {comments
                .filter((comment) => !comment.parentCommentId)
                .slice(0, 4)
                .map((comment) => (
                  <div className="lt-mobile-comment" key={comment.id}>
                    <Avatar name={comment.author?.name} />
                    <div>
                      <strong>{comment.author?.name || 'Someone'}</strong>
                      <p>{comment.body}</p>
                    </div>
                  </div>
                ))}
            </>
          ) : (
            <>
              <span aria-hidden="true">
                <ChatBubbleOutlineIcon />
              </span>
              <strong>No thoughts shared yet</strong>
              <p>
                Be the first to leave a whisper in the margins of this memory.
              </p>
            </>
          )}
        </div>
      </div>
      <footer className="lt-mobile-composer">
        <Avatar name="Y" />
        <span>Leave a thought in the margins…</span>
        <button disabled aria-label="Send thought (preview only)">
          <ArrowForwardIcon fontSize="small" />
        </button>
      </footer>
    </section>
  );
}
